// Recursive subtask tree. Replaces the flat 2-level table inside
// ProjectRow with a depth-arbitrary view, so chain-split tasks
// (Regina → 待办 → 店长 → 子分配) become visible in one place
// instead of forcing a drill-into-detail for each level.
//
// One row per task. Children render directly below with extra indent.
// Each row with children gets a chevron; click toggles inline expansion.
// Layout uses CSS flex with fixed widths so deep nests stay aligned.
//
// Drag-to-reorder: each row is draggable and can be reordered WITHIN its own
// sibling group (same parent only). We reuse the main-task reorder endpoint
// (`reorderTasks` sets sort_order for the passed ids), optimistically apply a
// per-parent order override, and revert on failure. Drag events stopPropagation
// so they never trigger the enclosing ProjectRow's own drag handlers.

import { useState } from 'react';
import * as api from '../lib/api.ts';
import { Avatar } from './Avatar.jsx';
import { getResponsibles } from '../lib/owners.ts';
import { Prio, StatusPill } from './atoms.jsx';
import { SubtaskPanel } from './SubtaskPanel.jsx';
import { isOverdue, fmtShort } from '../lib/dates.ts';

function childrenOf(taskId, allTasks) {
  return allTasks
    .filter(t => t.parentId === taskId)
    .sort((a, b) => {
      const ao = a.sortOrder ?? 0, bo = b.sortOrder ?? 0;
      if (ao !== bo) return ao - bo;
      return (a.createdAt || '').localeCompare(b.createdAt || '');
    });
}

const SubtaskNode = ({ task, allTasks, staff, depth, onOpen, onPeek, defaultExpanded, dnd }) => {
  const kids = dnd.orderedChildren(task.id);
  const [open, setOpen] = useState(defaultExpanded);
  // All 负责人 (R), 主负责人 first. getResponsibles falls back to the legacy
  // single assigneeId, so old tasks still render their one owner.
  const rOwners = getResponsibles(task).map(o => ({ ...o, s: staff.find(x => x.id === o.staffId) }));
  const primary = rOwners[0];
  const over = isOverdue(task);

  const doneCount = kids.filter(k => k.status === '已完成').length;
  const hasKids = kids.length > 0;
  const dragCls = dnd.draggingId === task.id ? ' is-dragging'
                : dnd.overId === task.id ? ' is-drag-over'
                : '';

  return (
    <>
      <div className={`subtree-row${over ? ' is-over' : ''}${dragCls}`}
           style={{ paddingLeft: 12 + depth * 18 }}
           draggable
           onDragStart={e => dnd.onDragStart(e, task)}
           onDragOver={e => dnd.onDragOver(e, task)}
           onDragLeave={e => dnd.onDragLeave(e, task)}
           onDrop={e => dnd.onDrop(e, task)}
           onDragEnd={dnd.onDragEnd}>
        <span className="subtree-drag" title="拖动排序" aria-hidden>⋮⋮</span>
        {hasKids ? (
          <button type="button" className="subtree-chevron"
                  onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
                  aria-expanded={open}
                  aria-label={open ? '折叠' : '展开'}>
            {open ? '▾' : '▸'}
          </button>
        ) : (
          <span className="subtree-chevron subtree-chevron-empty" aria-hidden>·</span>
        )}
        <button type="button" className="subtree-main" onClick={() => onPeek(task)}>
          <div className="subtree-owner">
            {rOwners.length === 0 ? (
              <>
                <Avatar staff={undefined}/>
                <div className="subtree-owner-meta"><div className="subtree-owner-name">—</div></div>
              </>
            ) : (
              <>
                <span className="subtree-owner-avatars"
                      title={rOwners.map(o => o.s?.name || o.staffId).join('、')}>
                  {rOwners.map(o => (
                    <span key={o.staffId} className={`subtree-owner-av${o.isPrimary ? ' is-primary' : ''}`}>
                      <Avatar staff={o.s}/>
                      {o.isPrimary && rOwners.length > 1 && <span className="subtree-owner-star" aria-label="主负责人">★</span>}
                    </span>
                  ))}
                </span>
                <div className="subtree-owner-meta">
                  <div className="subtree-owner-name">
                    {rOwners.length === 1
                      ? (primary.s?.name || primary.staffId)
                      : `${primary.s?.name || primary.staffId} 等 ${rOwners.length} 人`}
                  </div>
                  <div className="subtree-owner-title">
                    {rOwners.length === 1 ? (primary.s?.title || '') : `${rOwners.length} 位负责人`}
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="subtree-title">
            {task.title}
            {hasKids && (
              <span className="subtree-kids-pill mono"> {doneCount}/{kids.length}</span>
            )}
          </div>
          <div className="subtree-prio"><Prio value={task.priority}/></div>
          <div className={`subtree-due mono${over ? ' is-over' : ''}`}>
            {fmtShort(task.dueDate)}{over && ' · 逾期'}
          </div>
          <div className="subtree-status"><StatusPill value={task.status}/></div>
        </button>
      </div>
      {open && hasKids && kids.map(k => (
        <SubtaskNode key={k.id} task={k} allTasks={allTasks} staff={staff}
                     depth={depth + 1} onOpen={onOpen} onPeek={onPeek} defaultExpanded={false} dnd={dnd} />
      ))}
    </>
  );
};

/**
 * Render the immediate children of `parent` recursively. Used by
 * ProjectRow when the parent header is expanded. Owns the sibling
 * drag-reorder state for the whole subtree.
 */
export const SubtaskTree = ({ parent, allTasks, staff, onOpen, updateTask, addUpdate }) => {
  const [draggingId, setDraggingId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [orders, setOrders] = useState({}); // { parentId: [ids] } optimistic override
  const [peek, setPeek] = useState(null);   // subtask being previewed in the side panel

  function orderedChildren(parentId) {
    const base = childrenOf(parentId, allTasks);
    const ov = orders[parentId];
    if (!ov) return base;
    const byId = new Map(base.map(t => [t.id, t]));
    const ordered = ov.map(id => byId.get(id)).filter(Boolean);
    // Append any siblings created after the override was captured.
    for (const t of base) if (!ov.includes(t.id)) ordered.push(t);
    return ordered;
  }

  function onDragStart(e, task) {
    e.stopPropagation();
    setDraggingId(task.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task.id);
  }
  function onDragOver(e, task) {
    if (draggingId == null) return;
    const src = allTasks.find(t => t.id === draggingId);
    if (!src || src.parentId !== task.parentId) return; // siblings only
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (task.id !== overId) setOverId(task.id);
  }
  function onDragLeave(e, task) {
    e.stopPropagation();
    if (task.id === overId) setOverId(null);
  }
  function onDragEnd() { setDraggingId(null); setOverId(null); }
  async function onDrop(e, target) {
    e.preventDefault();
    e.stopPropagation();
    const sourceId = draggingId || e.dataTransfer.getData('text/plain');
    setDraggingId(null); setOverId(null);
    if (!sourceId || sourceId === target.id) return;
    const src = allTasks.find(t => t.id === sourceId);
    if (!src || src.parentId !== target.parentId) return; // siblings only
    const sibs = orderedChildren(target.parentId).map(t => t.id);
    const from = sibs.indexOf(sourceId);
    const to = sibs.indexOf(target.id);
    if (from === -1 || to === -1) return;
    const next = [...sibs];
    next.splice(from, 1);
    next.splice(to, 0, sourceId);
    setOrders(o => ({ ...o, [target.parentId]: next })); // optimistic
    try {
      await api.reorderTasks(next);
    } catch (err) {
      console.warn('subtask reorder failed:', err.message);
      setOrders(o => { const n = { ...o }; delete n[target.parentId]; return n; }); // revert
    }
  }

  const dnd = { draggingId, overId, orderedChildren, onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd };

  const kids = orderedChildren(parent.id);
  if (kids.length === 0) {
    return (
      <div className="subtree-empty">没有子项</div>
    );
  }
  return (
    <>
    <div className="subtree-root">
      <div className="subtree-row subtree-header">
        <span className="subtree-drag subtree-drag-empty" aria-hidden></span>
        <span className="subtree-chevron subtree-chevron-empty" aria-hidden></span>
        <div className="subtree-main subtree-main-header">
          <div className="subtree-owner-h">负责人</div>
          <div className="subtree-title-h">子项</div>
          <div className="subtree-prio-h">优先级</div>
          <div className="subtree-due-h">截止</div>
          <div className="subtree-status-h">状态</div>
        </div>
      </div>
      {kids.map(k => (
        <SubtaskNode key={k.id} task={k} allTasks={allTasks} staff={staff}
                     depth={0} onOpen={onOpen} onPeek={setPeek} defaultExpanded={false} dnd={dnd} />
      ))}
    </div>
    {peek && (
      <SubtaskPanel task={allTasks.find(t => t.id === peek.id) || peek} staff={staff}
                    updateTask={updateTask} addUpdate={addUpdate}
                    onClose={() => setPeek(null)} onOpenFull={onOpen} />
    )}
    </>
  );
};
